// @ts-nocheck

import type { ReactNode } from "react";
import React from "react";
import {
  FaSpa, FaPalette, FaScissors, FaWineBottle, FaPumpSoap, FaWandMagicSparkles, FaGem, FaPlug,
  FaBoxesStacked, FaSquarePlus, FaVest, FaTag, FaMitten, FaLayerGroup,
} from "react-icons/fa6";

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */
export type AttributeInput = "select" | "multiselect" | "text" | "number" | "color";

export interface AttributeOption {
  id: number;
  name?: string;
  hex?: string;
  symbol?: string;
}

export interface Attribute {
  id: number;
  name: string;
  inputType?: AttributeInput; // default "select"
  required?: boolean;
  unit?: string;              // for number inputs, e.g. "ml", "g"
  options: AttributeOption[]; // [] for free-text / number
}

export interface AttributeItem {
  id: number;
  name: string;
  icon?: ReactNode;
}

export interface CategoryItem {
  id: string;
  parentId: string | null;
  level: "department" | "category" | "subCategory" | "productType" | "product";
  name: string;
  icon?: ReactNode;
  attributes?: Attribute[];
  children?: CategoryItem[];
}

export interface ColorItem {
  id: number;
  name: string;
  hexCodes?: string[];
}

export interface UnitItem {
  id: number;
  name: string;
  symbol?: string;
}

export interface UnitCategory {
  id: string;
  name: string;
  units: UnitItem[];
}

/* ═══════════════════════════════════════════════════════════
   UNITS
═══════════════════════════════════════════════════════════ */
export const Units: UnitCategory[] = [
  /* ───── BEAUTY ESSENTIALS ───── */
  {
    id: "volume",
    name: "Volume",
    units: [
      { id: 14001, name: "Milliliter", symbol: "ml" },
      { id: 14002, name: "Liter", symbol: "L" },
      { id: 14003, name: "Deciliter", symbol: "dL" },
      { id: 14004, name: "Fluid ounce", symbol: "fl oz" },
    ],
  },
  {
    id: "percentage",
    name: "Percentage",
    units: [
      { id: 15001, name: "Percent", symbol: "%" },
    ],
  },
  {
    id: "count",
    name: "Count",
    units: [
      { id: 16001, name: "Pieces", symbol: "pcs" },
      { id: 16002, name: "Sheets", symbol: "sheets" },
      { id: 16003, name: "Shades", symbol: "shades" },
    ],
  },
  {
    id: "shelf_life",
    name: "Shelf life",
    units: [
      { id: 17001, name: "Month", symbol: "months" },
      { id: 17002, name: "Year", symbol: "years" },
    ],
  },
  {
    id: "power",
    name: "Power",
    units: [
      { id: 18001, name: "Watt", symbol: "W" },
      { id: 18002, name: "Kilowatt", symbol: "kW" },
    ],
  },

  /* ───── LENGTH ───── */
  {
    id: "length",
    name: "Length",
    units: [
      { id: 1001, name: "Meter", symbol: "m" },
      { id: 1002, name: "Centimeter", symbol: "cm" },
      { id: 1003, name: "Millimeter", symbol: "mm" },
      { id: 1004, name: "Foot", symbol: "ft" },
      { id: 1005, name: "Inch", symbol: "in" },
    ],
  },

  /* ───── MASS ───── */
  {
    id: "mass",
    name: "Mass",
    units: [
      { id: 2001, name: "Kilogram", symbol: "kg" },
      { id: 2002, name: "Tonne", symbol: "t" },
      { id: 2003, name: "Gram", symbol: "g" },
      { id: 2004, name: "Pound-mass", symbol: "lbm" },
      { id: 2005, name: "Slug", symbol: "sl" },
      { id: 2007, name: "Ounce", symbol: "oz" },
    ],
  },

  /* ───── ENERGY ───── */
  {
    id: "energy",
    name: "Energy",
    units: [
      { id: 3001, name: "Joule", symbol: "J" },
      { id: 3002, name: "Kilojoule", symbol: "kJ" },
      { id: 3003, name: "BTU", symbol: "Btu" },
      { id: 3004, name: "Foot-pound force", symbol: "ft·lbf" },
      { id: 3005, name: "Calorie", symbol: "cal" },
    ],
  },

  /* ───── MODULUS / STRESS ───── */
  {
    id: "modulus_stress",
    name: "Modulus/Stress",
    units: [
      { id: 4001, name: "Pascal", symbol: "Pa" },
      { id: 4002, name: "Kilopascal", symbol: "kPa" },
      { id: 4003, name: "Megapascal", symbol: "MPa" },
      { id: 4004, name: "Kilogram per square centimeter", symbol: "kg/cm²" },
      { id: 4005, name: "PSI", symbol: "psi" },
      { id: 4006, name: "KSI", symbol: "ksi" },
      { id: 4007, name: "PSF", symbol: "psf" },
    ],
  },

  /* ───── FORCE ───── */
  {
    id: "force",
    name: "Force",
    units: [
      { id: 5001, name: "Newton", symbol: "N" },
      { id: 5002, name: "Kilonewton", symbol: "kN" },
      { id: 5003, name: "Meganewton", symbol: "MN" },
      { id: 5004, name: "Kilogram-force", symbol: "kgf" },
      { id: 5005, name: "Pound-force", symbol: "lbf" },
      { id: 5006, name: "Kip", symbol: "kip" },
    ],
  },

  /* ───── TEMPERATURE ───── */
  {
    id: "temperature",
    name: "Temperature",
    units: [
      { id: 6001, name: "Celsius", symbol: "°C" },
      { id: 6002, name: "Fahrenheit", symbol: "°F" },
      { id: 6003, name: "Kelvin", symbol: "K" },
      { id: 6004, name: "Rankine", symbol: "°R" },
    ],
  },

  /* ───── TIME ───── */
  {
    id: "time",
    name: "Time",
    units: [
      { id: 7001, name: "Second", symbol: "s" },
      { id: 7002, name: "Minute", symbol: "min" },
      { id: 7003, name: "Hour", symbol: "h" },
      { id: 7004, name: "Day", symbol: "days" },
    ],
  },

  /* ───── DENSITY ───── */
  {
    id: "density",
    name: "Density",
    units: [
      { id: 8001, name: "Kilogram per cubic meter", symbol: "kg/m³" },
    ],
  },

  /* ───── CONDUCTIVITY ───── */
  {
    id: "conductivity",
    name: "Conductivity",
    units: [
      { id: 9001, name: "Joule per second centimeter Kelvin", symbol: "J/(s·cm·K)" },
    ],
  },

  /* ───── SPECIFIC HEAT ───── */
  {
    id: "specific_heat",
    name: "Specific Heat",
    units: [
      { id: 10001, name: "Joule per kilogram Kelvin", symbol: "J/(kg·K)" },
    ],
  },

  /* ───── FLUX ───── */
  {
    id: "flux",
    name: "Flux",
    units: [
      { id: 11001, name: "Joule per second square meter", symbol: "J/(s·m²)" },
    ],
  },

  /* ───── CONVECTION ───── */
  {
    id: "convection",
    name: "Convection",
    units: [
      { id: 12001, name: "Joule per second square meter Kelvin", symbol: "J/(s·m²·K)" },
    ],
  },

  /* ───── PRESETS ───── */
  {
    id: "presets",
    name: "Presets",
    units: [
      { id: 13001, name: "SI" },
      { id: 13002, name: "N·mm" },
      { id: 13003, name: "IPS" },
      { id: 13004, name: "kN·m" },
      { id: 13005, name: "EPS" },
      { id: 13006, name: "kg·cm" },
      { id: 13007, name: "User" },
    ],
  },
];


/* ═══════════════════════════════════════════════════════════
   📦 PACKAGING TYPE
═══════════════════════════════════════════════════════════ */
export const PackagingTypes: AttributeItem[] = [
  { id: 90301, name: "Bottle",      icon: <FaVest size={14} /> },
  { id: 90302, name: "Pump Bottle", icon: <FaVest size={14} /> },
  { id: 90303, name: "Spray",       icon: <FaVest size={14} /> },
  { id: 90304, name: "Jar",         icon: <FaSquarePlus size={14} /> },
  { id: 90305, name: "Tube",        icon: <FaTag size={14} /> },
  { id: 90306, name: "Stick",       icon: <FaTag size={14} /> },
  { id: 90307, name: "Compact",     icon: <FaLayerGroup size={14} /> },
  { id: 90308, name: "Sachet",      icon: <FaMitten size={14} /> },
  { id: 90309, name: "Box",         icon: <FaBoxesStacked size={14} /> },
  { id: 90310, name: "Pouch",       icon: <FaMitten size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   COLORS
═══════════════════════════════════════════════════════════ */
export const Colors: ColorItem[] = [
  /* NEUTRALS */
  { id: 9000110, name: "Off White",     hexCodes: ["#F8F8F0"] },
  { id: 9000111, name: "Pearl",         hexCodes: ["#EAE0C8"] },
  { id: 9000112, name: "Stone",         hexCodes: ["#B8B5A9"] },
  { id: 9000113, name: "Taupe",         hexCodes: ["#483C32"] },
  { id: 9000114, name: "Sand",          hexCodes: ["#C2B280"] },
  { id: 9000115, name: "Khaki",         hexCodes: ["#C3B091"] },
  { id: 9000116, name: "Camel",         hexCodes: ["#C19A6B"] },
  { id: 9000117, name: "Mocha",         hexCodes: ["#967969"] },
  { id: 9000118, name: "Chocolate",     hexCodes: ["#7B3F00"] },
  { id: 9000119, name: "Espresso",      hexCodes: ["#4B3621"] },

  /* BLACK & WHITE / GRAYSCALE */
  { id: 9001101, name: "Black",         hexCodes: ["#000000"] },
  { id: 9001102, name: "Licorice",      hexCodes: ["#0C0C0C"] },
  { id: 9001103, name: "Jet Black",     hexCodes: ["#191919"] },
  { id: 9001104, name: "Graphite",      hexCodes: ["#262626"] },
  { id: 9001105, name: "Charcoal",      hexCodes: ["#323232"] },
  { id: 9001106, name: "Dark Gray",     hexCodes: ["#4C4C4C"] },
  { id: 9001107, name: "Ash Gray",      hexCodes: ["#666666"] },
  { id: 9001108, name: "Medium Gray",   hexCodes: ["#7F7F7F"] },
  { id: 9001109, name: "Silver Gray",   hexCodes: ["#999999"] },
  { id: 9001110, name: "Light Gray",    hexCodes: ["#B2B2B2"] },
  { id: 9001111, name: "Gainsboro",     hexCodes: ["#CCCCCC"] },
  { id: 9001112, name: "Smoke",         hexCodes: ["#E5E5E5"] },
  { id: 9001113, name: "White",         hexCodes: ["#FFFFFF"] },

  /* REDS */
  { id: 9000401, name: "Pure Red",       hexCodes: ["#FF0000"] },
  { id: 9000402, name: "Vivid Red",      hexCodes: ["#E60000"] },
  { id: 9000403, name: "True Red",       hexCodes: ["#CF0101"] },
  { id: 9000404, name: "Bold Red",       hexCodes: ["#C00000"] },
  { id: 9000405, name: "Dark Red",       hexCodes: ["#A60202"] },
  { id: 9000416, name: "Light Coral",    hexCodes: ["#FF8B8E"] },
  { id: 9000417, name: "Neon Red",       hexCodes: ["#FF3D41"] },
  { id: 9000418, name: "Firetruck Red",  hexCodes: ["#DC0005"] },
  { id: 9000419, name: "Deep Red",       hexCodes: ["#C80004"] },
  { id: 9000420, name: "Maroon",         hexCodes: ["#B40000"] },
  { id: 9000421, name: "Burgundy",       hexCodes: ["#690000"] },
  { id: 9000422, name: "Flame Red",      hexCodes: ["#E31919"] },
  { id: 9000423, name: "Vermilion",      hexCodes: ["#EA2300"] },
  { id: 9000424, name: "Ruby Red",       hexCodes: ["#DD1212"] },
  { id: 9000425, name: "Bright Red",     hexCodes: ["#F02525"] },
  { id: 9000426, name: "Hot Red",        hexCodes: ["#EA0A0A"] },
  { id: 9000427, name: "Candy Red",      hexCodes: ["#DD1717"] },
  { id: 9000428, name: "Tomato Red",     hexCodes: ["#FB3310"] },
  { id: 9000429, name: "Rose Red",       hexCodes: ["#F40D30"] },
  { id: 9000430, name: "Cardinal",       hexCodes: ["#C70E0E"] },
  { id: 9000431, name: "Raspberry",      hexCodes: ["#D6204E"] },
  { id: 9000406, name: "Crimson",        hexCodes: ["#DC143C"] },
  { id: 9000407, name: "Scarlet",        hexCodes: ["#FF2400"] },
  { id: 9000408, name: "Cherry Red",     hexCodes: ["#D2042D"] },
  { id: 9000409, name: "Wine",           hexCodes: ["#722F37"] },
  { id: 9000410, name: "Coral",          hexCodes: ["#FF7F50"] },
  { id: 9000411, name: "Salmon",         hexCodes: ["#FA8072"] },
  { id: 9000412, name: "Blush Pink",     hexCodes: ["#F4C2C2"] },
  { id: 9000413, name: "Hot Pink",       hexCodes: ["#FF69B4"] },
  { id: 9000414, name: "Fuchsia",        hexCodes: ["#FF00FF"] },
  { id: 9000415, name: "Magenta",        hexCodes: ["#D1007A"] },

  /* PINKS */
  { id: 9001201, name: "Electric Pink",  hexCodes: ["#FB00BE"] },
  { id: 9001202, name: "Neon Pink",      hexCodes: ["#FF0074"] },
  { id: 9001203, name: "Bright Pink",    hexCodes: ["#FF00CE"] },
  { id: 9001204, name: "Deep Pink",      hexCodes: ["#FF07A9"] },
  { id: 9001205, name: "Cerise",         hexCodes: ["#EE006C"] },

  /* BERRY */
  { id: 9001301, name: "Plum Berry",     hexCodes: ["#990F4B"] },
  { id: 9001302, name: "Mulberry",       hexCodes: ["#AD3D5E"] },
  { id: 9001303, name: "Dusty Rose",     hexCodes: ["#C05F72"] },
  { id: 9001304, name: "Mauve Pink",     hexCodes: ["#D27F87"] },
  { id: 9001305, name: "Blush Berry",    hexCodes: ["#E39E9C"] },

  /* ORANGES & YELLOWS */
  { id: 9000505, name: "Peach",          hexCodes: ["#FFE5B4"] },
  { id: 9000506, name: "Apricot",        hexCodes: ["#FBCEB1"] },
  { id: 9000507, name: "Tangerine",      hexCodes: ["#F28500"] },
  { id: 9000508, name: "Burnt Orange",   hexCodes: ["#CC5500"] },
  { id: 9000509, name: "Rust",           hexCodes: ["#B7410E"] },
  { id: 9000510, name: "Golden Yellow",  hexCodes: ["#FFDF00"] },
  { id: 9000511, name: "Lemon",          hexCodes: ["#FFF44F"] },
  { id: 9000512, name: "Canary Yellow",  hexCodes: ["#FFEF00"] },
  { id: 9000513, name: "Sunflower",      hexCodes: ["#FFC512"] },

  /* ORANGES (EXTENDED) */
  { id: 9001401, name: "Burnt Sienna",   hexCodes: ["#C65102"] },
  { id: 9001402, name: "Amber Orange",   hexCodes: ["#D56602"] },
  { id: 9001403, name: "Dark Amber",     hexCodes: ["#E37B01"] },
  { id: 9001404, name: "Deep Amber",     hexCodes: ["#F19001"] },
  { id: 9001405, name: "Orange",         hexCodes: ["#FFA500"] },
  { id: 9001406, name: "Pale Orange",    hexCodes: ["#FDCC99"] },
  { id: 9001407, name: "Warm Orange",    hexCodes: ["#FB9932"] },
  { id: 9001408, name: "Pumpkin",        hexCodes: ["#FA8000"] },
  { id: 9001409, name: "Bronze Orange",  hexCodes: ["#C86600"] },
  { id: 9001410, name: "Dark Pumpkin",   hexCodes: ["#7D4000"] },

  /* YELLOWS (EXTENDED) */
  { id: 9001501, name: "Pale Yellow",    hexCodes: ["#FCF5B2"] },
  { id: 9001502, name: "Soft Yellow",    hexCodes: ["#FBEE7F"] },
  { id: 9001503, name: "Bright Yellow",  hexCodes: ["#F9E432"] },
  { id: 9001504, name: "Pure Yellow",    hexCodes: ["#F8DE00"] },
  { id: 9001505, name: "Gold Yellow",    hexCodes: ["#DFC700"] },
  { id: 9001506, name: "Cream Yellow",   hexCodes: ["#FFFBA0"] },
  { id: 9001507, name: "Butter",         hexCodes: ["#FFEC8E"] },
  { id: 9001508, name: "Straw",          hexCodes: ["#FEE283"] },
  { id: 9001509, name: "Honey",          hexCodes: ["#F6D484"] },
  { id: 9001510, name: "Warm Honey",     hexCodes: ["#EFC576"] },

  /* GREENS */
  { id: 9000305, name: "Emerald",        hexCodes: ["#50C878"] },
  { id: 9000306, name: "Jade",           hexCodes: ["#00A86B"] },
  { id: 9000307, name: "Sage",           hexCodes: ["#9CAF88"] },
  { id: 9000308, name: "Moss Green",     hexCodes: ["#8A9A5B"] },
  { id: 9000309, name: "Army Green",     hexCodes: ["#4B5320"] },
  { id: 9000310, name: "Pistachio",      hexCodes: ["#93C572"] },
  { id: 9000311, name: "Sea Green",      hexCodes: ["#2E8B57"] },
  { id: 9000312, name: "Kelly Green",    hexCodes: ["#4CBB17"] },
  { id: 9000313, name: "Neon Green",     hexCodes: ["#39FF14"] },
  { id: 9000314, name: "Dark Green",     hexCodes: ["#006400"] },

  /* GREENS (EXTENDED) */
  { id: 9001601, name: "Mint",           hexCodes: ["#CDF5D2"] },
  { id: 9001602, name: "Spring Green",   hexCodes: ["#69E279"] },
  { id: 9001603, name: "Vivid Green",    hexCodes: ["#04A519"] },
  { id: 9001604, name: "Forest Green",   hexCodes: ["#049016"] },
  { id: 9001605, name: "Deep Forest",    hexCodes: ["#013E09"] },
  { id: 9001606, name: "Lime Green",     hexCodes: ["#00FF00"] },
  { id: 9001607, name: "Bright Lime",    hexCodes: ["#05C800"] },
  { id: 9001608, name: "Grass Green",    hexCodes: ["#069300"] },
  { id: 9001609, name: "Fern",           hexCodes: ["#056200"] },
  { id: 9001610, name: "Pine",           hexCodes: ["#033500"] },
  { id: 9001611, name: "Laurel",         hexCodes: ["#AAD688"] },
  { id: 9001612, name: "Artichoke",      hexCodes: ["#98C377"] },
  { id: 9001613, name: "Herb Green",     hexCodes: ["#8BBD78"] },
  { id: 9001614, name: "Leaf Green",     hexCodes: ["#5EA758"] },
  { id: 9001615, name: "Dark Sage",      hexCodes: ["#47894B"] },
  { id: 9001616, name: "Neon Lime",      hexCodes: ["#76FF7B"] },
  { id: 9001617, name: "Lime Punch",     hexCodes: ["#A1FF7B"] },
  { id: 9001618, name: "Green Yellow",   hexCodes: ["#C4FF7B"] },
  { id: 9001619, name: "Yellow Lime",    hexCodes: ["#E3FF7A"] },
  { id: 9001620, name: "Lemon Lime",     hexCodes: ["#FFFE7A"] },

  /* BLUES */
  { id: 9000206, name: "Baby Blue",      hexCodes: ["#89CFF0"] },
  { id: 9000207, name: "Powder Blue",    hexCodes: ["#B0E0E6"] },
  { id: 9000208, name: "Steel Blue",     hexCodes: ["#4682B4"] },
  { id: 9000209, name: "Denim Blue",     hexCodes: ["#1560BD"] },
  { id: 9000210, name: "Cobalt Blue",    hexCodes: ["#0047AB"] },
  { id: 9000211, name: "Sapphire",       hexCodes: ["#0F52BA"] },
  { id: 9000212, name: "Aqua",           hexCodes: ["#00FFFF"] },
  { id: 9000213, name: "Cyan",           hexCodes: ["#00BCD4"] },
  { id: 9000214, name: "Petrol Blue",    hexCodes: ["#005F6A"] },
  { id: 9000215, name: "Ice Blue",       hexCodes: ["#D6F5FF"] },

  /* BLUES (EXTENDED) */
  { id: 9001701, name: "Midnight Blue",  hexCodes: ["#040273"] },
  { id: 9001702, name: "Navy",           hexCodes: ["#022477"] },
  { id: 9001703, name: "Royal Navy",     hexCodes: ["#0E3879"] },
  { id: 9001704, name: "Dark Blue",      hexCodes: ["#1F4A7C"] },
  { id: 9001705, name: "Ocean Blue",     hexCodes: ["#315B7D"] },
  { id: 9001706, name: "Cornflower",     hexCodes: ["#5170D7"] },
  { id: 9001707, name: "Periwinkle",     hexCodes: ["#6E8CDC"] },
  { id: 9001708, name: "Muted Blue",     hexCodes: ["#8DA6DF"] },
  { id: 9001709, name: "Sky Blue",       hexCodes: ["#1A84B8"] },
  { id: 9001710, name: "Azure",          hexCodes: ["#1A8CB8"] },
  { id: 9001711, name: "Cerulean",       hexCodes: ["#1A94B8"] },
  { id: 9001712, name: "Peacock Blue",   hexCodes: ["#1A9CB8"] },
  { id: 9001713, name: "Lagoon Blue",    hexCodes: ["#1AA4B8"] },

  /* BLUE GRAY */
  { id: 9001801, name: "Slate Blue",     hexCodes: ["#758DA3"] },
  { id: 9001802, name: "Storm Blue",     hexCodes: ["#859CB2"] },
  { id: 9001803, name: "Dusty Blue",     hexCodes: ["#95ABC2"] },
  { id: 9001804, name: "Glacier Blue",   hexCodes: ["#A6BAD2"] },
  { id: 9001805, name: "Pale Steel",     hexCodes: ["#B7C9E2"] },

  /* DENIM */
  { id: 9001901, name: "Dark Denim",     hexCodes: ["#071F35"] },
  { id: 9001902, name: "Indigo Denim",   hexCodes: ["#24496B"] },
  { id: 9001903, name: "Medium Denim",   hexCodes: ["#446D92"] },
  { id: 9001904, name: "Faded Denim",    hexCodes: ["#98B1C8"] },
  { id: 9001905, name: "Washed Denim",   hexCodes: ["#F7FAFE"] },

  /* AQUA BLUE */
  { id: 9002001, name: "Soft Aqua",      hexCodes: ["#90C0E0"] },
  { id: 9002002, name: "Light Aqua",     hexCodes: ["#80B8D8"] },
  { id: 9002003, name: "Vivid Aqua",     hexCodes: ["#3890C8"] },
  { id: 9002004, name: "Bright Aqua",    hexCodes: ["#60A8D0"] },
  { id: 9002005, name: "Pale Aqua",      hexCodes: ["#98C8E0"] },

  /* CYAN */
  { id: 9002101, name: "Electric Cyan",  hexCodes: ["#00FFFF"] },
  { id: 9002102, name: "Bright Cyan",    hexCodes: ["#00E1E1"] },
  { id: 9002103, name: "Medium Cyan",    hexCodes: ["#00C3C3"] },
  { id: 9002104, name: "Deep Cyan",      hexCodes: ["#00A7A7"] },
  { id: 9002105, name: "Dark Teal",      hexCodes: ["#008B8B"] },

  /* PURPLES */
  { id: 9000604, name: "Violet",         hexCodes: ["#8F00FF"] },
  { id: 9000605, name: "Lilac",          hexCodes: ["#C8A2C8"] },
  { id: 9000606, name: "Mauve",          hexCodes: ["#E0B0FF"] },
  { id: 9000607, name: "Orchid",         hexCodes: ["#DA70D6"] },
  { id: 9000608, name: "Eggplant",       hexCodes: ["#614051"] },
  { id: 9000609, name: "Deep Purple",    hexCodes: ["#512DA8"] },

  /* PURPLES (EXTENDED) */
  { id: 9002201, name: "Electric Purple", hexCodes: ["#B700FF"] },
  { id: 9002202, name: "Vivid Purple",   hexCodes: ["#9A00D6"] },
  { id: 9002203, name: "Grape",          hexCodes: ["#7900A8"] },
  { id: 9002204, name: "Dark Grape",     hexCodes: ["#600085"] },
  { id: 9002205, name: "Blackberry",     hexCodes: ["#300042"] },

  /* VIOLET / LAVENDER */
  { id: 9002301, name: "Soft Violet",    hexCodes: ["#D6B4FC"] },
  { id: 9002302, name: "Wisteria",       hexCodes: ["#E0BCF5"] },
  { id: 9002303, name: "Lavender",       hexCodes: ["#EAC3EE"] },
  { id: 9002304, name: "Pale Lavender",  hexCodes: ["#F4CAE7"] },
  { id: 9002305, name: "Blush Lavender", hexCodes: ["#FFD1DF"] },

  /* INDIGO */
  { id: 9002401, name: "Midnight Indigo", hexCodes: ["#191970"] },
  { id: 9002402, name: "Royal Indigo",   hexCodes: ["#002395"] },
  { id: 9002403, name: "Dark Indigo",    hexCodes: ["#00416A"] },
  { id: 9002404, name: "Indigo Blue",    hexCodes: ["#1560BD"] },
  { id: 9002405, name: "Soft Indigo",    hexCodes: ["#5D76CB"] },

  /* TRUE COLORS / HERITAGE */
  { id: 9002501, name: "Deep Crimson",   hexCodes: ["#830000"] },
  { id: 9002502, name: "Dark Sienna",    hexCodes: ["#8B4D00"] },
  { id: 9002503, name: "Olive Drab",     hexCodes: ["#666700"] },
  { id: 9002504, name: "Hunter Green",   hexCodes: ["#00510E"] },
  { id: 9002505, name: "Midnight Navy",  hexCodes: ["#000440"] },

  /* BROWNS */
  { id: 9000901, name: "Tan",            hexCodes: ["#D2B48C"] },
  { id: 9000902, name: "Chestnut",       hexCodes: ["#954535"] },
  { id: 9000903, name: "Walnut",         hexCodes: ["#5D432C"] },
  { id: 9000904, name: "Mahogany",       hexCodes: ["#C04000"] },
  { id: 9000905, name: "Cocoa",          hexCodes: ["#6F4E37"] },

  /* BROWNS (EXTENDED) */
  { id: 9002601, name: "Sandy Brown",    hexCodes: ["#FA9755"] },
  { id: 9002602, name: "Warm Brown",     hexCodes: ["#C77844"] },
  { id: 9002603, name: "Hazel",          hexCodes: ["#87522E"] },
  { id: 9002604, name: "Umber",          hexCodes: ["#7B4B2A"] },
  { id: 9002605, name: "Toffee",         hexCodes: ["#613B21"] },
  { id: 9002606, name: "Dark Toffee",    hexCodes: ["#553311"] },
  { id: 9002607, name: "Auburn Brown",   hexCodes: ["#664433"] },
  { id: 9002608, name: "Seal Brown",     hexCodes: ["#332211"] },
  { id: 9002609, name: "Caramel",        hexCodes: ["#996633"] },
  { id: 9002610, name: "Warm Caramel",   hexCodes: ["#CCAA66"] },
  { id: 9002611, name: "Ginger",         hexCodes: ["#CC8833"] },
  { id: 9002612, name: "Cognac",         hexCodes: ["#D08C42"] },
  { id: 9002613, name: "Whiskey",        hexCodes: ["#D4914F"] },
  { id: 9002614, name: "Teak",           hexCodes: ["#D9955B"] },
  { id: 9002615, name: "Butterscotch",   hexCodes: ["#DD9966"] },

  /* CHESTNUTS */
  { id: 9002701, name: "Dark Chestnut",  hexCodes: ["#742802"] },
  { id: 9002702, name: "Russet",         hexCodes: ["#8F4934"] },
  { id: 9002703, name: "Clay",           hexCodes: ["#A96A5C"] },
  { id: 9002704, name: "Rosy Brown",     hexCodes: ["#C38B85"] },
  { id: 9002705, name: "Pale Chestnut",  hexCodes: ["#DDADAF"] },

  /* METALLICS */
  { id: 9000701, name: "Gold",           hexCodes: ["#D4AF37"] },
  { id: 9000702, name: "Rose Gold",      hexCodes: ["#B76E79"] },
  { id: 9000703, name: "Copper",         hexCodes: ["#B87333"] },
  { id: 9000704, name: "Bronze",         hexCodes: ["#CD7F32"] },
  { id: 9000705, name: "Gunmetal",       hexCodes: ["#2A3439"] },
  { id: 9000706, name: "Titanium",       hexCodes: ["#878681"] },
  { id: 9000707, name: "Platinum",       hexCodes: ["#E5E4E2"] },
  { id: 9000708, name: "Chrome",         hexCodes: ["#D7D7D7"] },

  /* PASTELS */
  { id: 9001001, name: "Pastel Blue",    hexCodes: ["#AEC6CF"] },
  { id: 9001002, name: "Pastel Green",   hexCodes: ["#77DD77"] },
  { id: 9001003, name: "Pastel Pink",    hexCodes: ["#FFD1DC"] },
  { id: 9001004, name: "Pastel Yellow",  hexCodes: ["#FDFD96"] },
  { id: 9001005, name: "Pastel Purple",  hexCodes: ["#C3B1E1"] },

  /* SPECIAL / MIXED */
  { id: 9000801, name: "Multicolor" },
  { id: 9000802, name: "Transparent" },
  { id: 9000803, name: "Matte Black",    hexCodes: ["#1C1C1C"] },
  { id: 9000804, name: "Glossy White",   hexCodes: ["#FAFAFA"] },

  /* SPECIAL EFFECTS */
  { id: 9000805, name: "Rainbow" },
  { id: 9000806, name: "Iridescent" },
  { id: 9000807, name: "Holographic" },
  { id: 9000808, name: "Metallic" },
  { id: 9000809, name: "Glow in the Dark" },
  { id: 9000810, name: "Clear",          hexCodes: ["#FFFFFF"] },
];


/* ═══════════════════════════════════════════════════════════
   01 BEAUTY
   dept attrs 10001+ · category attrs: 101xx..109xx
   Categories only — no product names. Deeper ids (010101, 01010101 …)
   inherit from their category via attributesForCategory().
═══════════════════════════════════════════════════════════ */
export const DEPARTMENTBEAUTYATTRIBUTES: CategoryItem[] = [
  {
    id: "0101", parentId: "01", level: "category", name: "Skincare", icon: <FaSpa />,
    attributes: [
      {
        id: 10101, name: "Skin Type", inputType: "select", required: true,
        options: [
          { id: 1, name: "Normal" }, { id: 2, name: "Dry" }, { id: 3, name: "Oily" },
          { id: 4, name: "Combination" }, { id: 5, name: "Sensitive" }, { id: 6, name: "All Skin Types" },
        ],
      },
      {
        id: 10102, name: "Skin Concern", inputType: "multiselect",
        options: [
          { id: 1, name: "Acne" }, { id: 2, name: "Anti-Aging" }, { id: 3, name: "Dark Spots" },
          { id: 4, name: "Dullness" }, { id: 5, name: "Dehydration" }, { id: 6, name: "Redness" },
          { id: 7, name: "Large Pores" }, { id: 8, name: "Dark Circles" },
        ],
      },
      {
        id: 10103, name: "Key Ingredient", inputType: "multiselect",
        options: [
          { id: 1, name: "Vitamin C" }, { id: 2, name: "Hyaluronic Acid" }, { id: 3, name: "Niacinamide" },
          { id: 4, name: "Retinol" }, { id: 5, name: "Peptides" }, { id: 6, name: "Ceramides" },
          { id: 7, name: "AHA / BHA" }, { id: 8, name: "Salicylic Acid" },
        ],
      },
      {
        id: 10104, name: "SPF", inputType: "select",
        options: [
          { id: 1, name: "None" }, { id: 2, name: "SPF 15" }, { id: 3, name: "SPF 30" },
          { id: 4, name: "SPF 50" }, { id: 5, name: "SPF 50+" },
        ],
      },
      {
        id: 10105, name: "Fragrance-Free", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10106, name: "Volume", inputType: "number", unit: "ml",
        options: [],
      },
    ],
  },
  {
    id: "0102", parentId: "01", level: "category", name: "Makeup", icon: <FaPalette />,
    attributes: [
      {
        id: 10201, name: "Shade Name", inputType: "text",
        options: [],
      },
      {
        id: 10202, name: "Finish", inputType: "select",
        options: [
          { id: 1, name: "Matte" }, { id: 2, name: "Dewy" }, { id: 3, name: "Satin" },
          { id: 4, name: "Natural" }, { id: 5, name: "Glossy" }, { id: 6, name: "Shimmer" },
          { id: 7, name: "Metallic" },
        ],
      },
      {
        id: 10203, name: "Coverage", inputType: "select",
        options: [
          { id: 1, name: "Sheer" }, { id: 2, name: "Light" }, { id: 3, name: "Medium" },
          { id: 4, name: "Full" },
        ],
      },
      {
        id: 10204, name: "Undertone", inputType: "select",
        options: [
          { id: 1, name: "Cool" }, { id: 2, name: "Warm" }, { id: 3, name: "Neutral" },
        ],
      },
      {
        id: 10205, name: "Waterproof", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10206, name: "Long-Wearing", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10207, name: "Form", inputType: "select",
        options: [
          { id: 1, name: "Liquid" }, { id: 2, name: "Cream" }, { id: 3, name: "Powder" },
          { id: 4, name: "Stick" }, { id: 5, name: "Gel" }, { id: 6, name: "Pencil" },
        ],
      },
    ],
  },
  {
    id: "0103", parentId: "01", level: "category", name: "Haircare", icon: <FaScissors />,
    attributes: [
      {
        id: 10301, name: "Hair Type", inputType: "multiselect",
        options: [
          { id: 1, name: "Straight" }, { id: 2, name: "Wavy" }, { id: 3, name: "Curly" },
          { id: 4, name: "Coily" }, { id: 5, name: "All Hair Types" },
        ],
      },
      {
        id: 10302, name: "Hair Concern", inputType: "multiselect",
        options: [
          { id: 1, name: "Damaged" }, { id: 2, name: "Dry" }, { id: 3, name: "Frizz" },
          { id: 4, name: "Dandruff" }, { id: 5, name: "Hair Loss" }, { id: 6, name: "Oily Scalp" },
          { id: 7, name: "Color-Treated" }, { id: 8, name: "Thinning" },
        ],
      },
      {
        id: 10303, name: "Hold Level", inputType: "select",
        options: [
          { id: 1, name: "Light" }, { id: 2, name: "Medium" }, { id: 3, name: "Strong" },
          { id: 4, name: "Extra Strong" },
        ],
      },
      {
        id: 10304, name: "Sulfate-Free", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10305, name: "Volume", inputType: "number", unit: "ml",
        options: [],
      },
    ],
  },
  {
    id: "0104", parentId: "01", level: "category", name: "Fragrance", icon: <FaWineBottle />,
    attributes: [
      {
        id: 10401, name: "Concentration", inputType: "select", required: true,
        options: [
          { id: 1, name: "Eau de Cologne" }, { id: 2, name: "Eau de Toilette" }, { id: 3, name: "Eau de Parfum" },
          { id: 4, name: "Parfum" }, { id: 5, name: "Body Mist" },
        ],
      },
      {
        id: 10402, name: "Scent Family", inputType: "multiselect",
        options: [
          { id: 1, name: "Floral" }, { id: 2, name: "Fruity" }, { id: 3, name: "Woody" },
          { id: 4, name: "Oriental" }, { id: 5, name: "Fresh" }, { id: 6, name: "Citrus" },
          { id: 7, name: "Gourmand" }, { id: 8, name: "Musky" }, { id: 9, name: "Aquatic" },
        ],
      },
      {
        id: 10403, name: "Top Notes", inputType: "text",
        options: [],
      },
      {
        id: 10404, name: "Heart Notes", inputType: "text",
        options: [],
      },
      {
        id: 10405, name: "Base Notes", inputType: "text",
        options: [],
      },
      {
        id: 10406, name: "Volume", inputType: "number", required: true, unit: "ml",
        options: [],
      },
    ],
  },
  {
    id: "0105", parentId: "01", level: "category", name: "Bath & Body", icon: <FaPumpSoap />,
    attributes: [
      {
        id: 10501, name: "Skin Type", inputType: "select",
        options: [
          { id: 1, name: "Normal" }, { id: 2, name: "Dry" }, { id: 3, name: "Oily" },
          { id: 4, name: "Combination" }, { id: 5, name: "Sensitive" }, { id: 6, name: "All Skin Types" },
        ],
      },
      {
        id: 10502, name: "Scent", inputType: "text",
        options: [],
      },
      {
        id: 10503, name: "Fragrance-Free", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10504, name: "Format", inputType: "select",
        options: [
          { id: 1, name: "Liquid" }, { id: 2, name: "Gel" }, { id: 3, name: "Cream" },
          { id: 4, name: "Bar" }, { id: 5, name: "Spray" }, { id: 6, name: "Roll-On" },
          { id: 7, name: "Stick" }, { id: 8, name: "Foam" }, { id: 9, name: "Salt" },
          { id: 10, name: "Oil" },
        ],
      },
      {
        id: 10505, name: "Volume", inputType: "number", unit: "ml",
        options: [],
      },
    ],
  },
  {
    id: "0106", parentId: "01", level: "category", name: "Nail Care", icon: <FaWandMagicSparkles />,
    attributes: [
      {
        id: 10601, name: "Shade Name", inputType: "text",
        options: [],
      },
      {
        id: 10602, name: "Finish", inputType: "select",
        options: [
          { id: 1, name: "Glossy" }, { id: 2, name: "Matte" }, { id: 3, name: "Shimmer" },
          { id: 4, name: "Glitter" }, { id: 5, name: "Metallic" }, { id: 6, name: "Cream" },
        ],
      },
      {
        id: 10603, name: "Free-From", inputType: "select",
        options: [
          { id: 1, name: "3-Free" }, { id: 2, name: "5-Free" }, { id: 3, name: "7-Free" },
          { id: 4, name: "10-Free" }, { id: 5, name: "21-Free" },
        ],
      },
      {
        id: 10604, name: "Cure Type", inputType: "select",
        options: [
          { id: 1, name: "None" }, { id: 2, name: "LED" }, { id: 3, name: "UV" },
          { id: 4, name: "UV / LED" },
        ],
      },
      {
        id: 10605, name: "Nail Shape", inputType: "select",
        options: [
          { id: 1, name: "Almond" }, { id: 2, name: "Coffin" }, { id: 3, name: "Square" },
          { id: 4, name: "Round" }, { id: 5, name: "Stiletto" }, { id: 6, name: "Oval" },
        ],
      },
      {
        id: 10606, name: "Nails Per Set", inputType: "number", unit: "pcs",
        options: [],
      },
      {
        id: 10607, name: "Volume", inputType: "number", unit: "ml",
        options: [],
      },
    ],
  },
  {
    id: "0107", parentId: "01", level: "category", name: "Men's Grooming", icon: <FaScissors />,
    attributes: [
      {
        id: 10701, name: "Skin Type", inputType: "select",
        options: [
          { id: 1, name: "Normal" }, { id: 2, name: "Dry" }, { id: 3, name: "Oily" },
          { id: 4, name: "Combination" }, { id: 5, name: "Sensitive" }, { id: 6, name: "All Skin Types" },
        ],
      },
      {
        id: 10702, name: "Scent", inputType: "text",
        options: [],
      },
      {
        id: 10703, name: "Alcohol-Free", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10704, name: "Number of Blades", inputType: "number", unit: "pcs",
        options: [],
      },
      {
        id: 10705, name: "Volume", inputType: "number", unit: "ml",
        options: [],
      },
    ],
  },
  {
    id: "0108", parentId: "01", level: "category", name: "Beauty Tools & Accessories", icon: <FaGem />,
    attributes: [
      {
        id: 10801, name: "Material", inputType: "select",
        options: [
          { id: 1, name: "Stainless Steel" }, { id: 2, name: "Wood" }, { id: 3, name: "Plastic" },
          { id: 4, name: "Silicone" }, { id: 5, name: "Fabric" }, { id: 6, name: "Glass" },
          { id: 7, name: "Natural Stone" },
        ],
      },
      {
        id: 10802, name: "Bristle Type", inputType: "select",
        options: [
          { id: 1, name: "Synthetic" }, { id: 2, name: "Natural" }, { id: 3, name: "Mixed" },
          { id: 4, name: "None" },
        ],
      },
      {
        id: 10803, name: "Size", inputType: "select",
        options: [
          { id: 1, name: "Small" }, { id: 2, name: "Medium" }, { id: 3, name: "Large" },
        ],
      },
      {
        id: 10804, name: "Washable", inputType: "select",
        options: [
          { id: 1, name: "Yes" }, { id: 2, name: "No" },
        ],
      },
      {
        id: 10805, name: "Pieces in Pack", inputType: "number", unit: "pcs",
        options: [],
      },
    ],
  },
  {
    id: "0109", parentId: "01", level: "category", name: "Beauty Appliances", icon: <FaPlug />,
    attributes: [
      {
        id: 10901, name: "Cord", inputType: "select",
        options: [
          { id: 1, name: "Corded" }, { id: 2, name: "Cordless" }, { id: 3, name: "Both" },
        ],
      },
      {
        id: 10902, name: "Power", inputType: "number", unit: "W",
        options: [],
      },
      {
        id: 10903, name: "Battery Runtime", inputType: "number", unit: "min",
        options: [],
      },
      {
        id: 10904, name: "Charging", inputType: "select",
        options: [
          { id: 1, name: "USB-C" }, { id: 2, name: "Charging Dock" }, { id: 3, name: "Mains Only" },
          { id: 4, name: "Replaceable Battery" },
        ],
      },
      {
        id: 10905, name: "Water Resistance", inputType: "select",
        options: [
          { id: 1, name: "None" }, { id: 2, name: "IPX4" }, { id: 3, name: "IP54" },
          { id: 4, name: "IP65" }, { id: 5, name: "IP67" }, { id: 6, name: "IP68" },
        ],
      },
      {
        id: 10906, name: "Heat Settings", inputType: "number", unit: "pcs",
        options: [],
      },
      {
        id: 10907, name: "Max Temperature", inputType: "number", unit: "°C",
        options: [],
      },
      {
        id: 10908, name: "Warranty", inputType: "select",
        options: [
          { id: 1, name: "No Warranty" }, { id: 2, name: "6 Months" }, { id: 3, name: "1 Year" },
          { id: 4, name: "2 Years" }, { id: 5, name: "3+ Years" },
        ],
      },
    ],
  },
];

export const DEPARTMENTBEAUTYTREE: CategoryItem = {
  id: "01",
  parentId: null,
  level: "department",
  name: "Beauty",
  icon: <FaSpa />,
  attributes: [
    {
      id: 10001, name: "Brand", inputType: "text", required: true,
      options: [],
    },
    {
      id: 10002, name: "Product Line", inputType: "text",
      options: [],
    },
    {
      id: 10003, name: "Suitable For", inputType: "multiselect",
      options: [
        { id: 1, name: "Women" }, { id: 2, name: "Men" }, { id: 3, name: "Unisex" },
        { id: 4, name: "Kids" },
      ],
    },
    {
      id: 10004, name: "Cruelty-Free", inputType: "select",
      options: [
        { id: 1, name: "Yes" }, { id: 2, name: "No" },
      ],
    },
    {
      id: 10005, name: "Vegan", inputType: "select",
      options: [
        { id: 1, name: "Yes" }, { id: 2, name: "No" },
      ],
    },
    {
      id: 10006, name: "Dermatologically Tested", inputType: "select",
      options: [
        { id: 1, name: "Yes" }, { id: 2, name: "No" },
      ],
    },
    {
      id: 10007, name: "Net Weight", inputType: "number", unit: "g",
      options: [],
    },
    {
      id: 10008, name: "Shelf Life After Opening", inputType: "number", unit: "months",
      options: [],
    },
  ],
  children: DEPARTMENTBEAUTYATTRIBUTES,
};

/* ═══════════════════════════════════════════════════════════
   ROOT — import THIS one (beauty only).
═══════════════════════════════════════════════════════════ */
export const DEPARTMENTS_ATTR_TREE: CategoryItem[] = [
  DEPARTMENTBEAUTYTREE,
];

const asOptions = (arr: any[]) => arr.map((x) => ({ id: x.id, name: x.name }));

export const ATTR_COLOR: Attribute = {
  id: 90001, name: "Color", inputType: "color",
  options: Colors.map((c) => ({ id: c.id, name: c.name, hex: c.hexCodes?.[0] })),
};
export const ATTR_PACKAGING: Attribute = { id: 90002, name: "Packaging", inputType: "select", options: asOptions(PackagingTypes) };
export const ATTR_UNIT: Attribute = {
  id: 90004, name: "Unit of measure", inputType: "select",
  options: Units.flatMap((g) =>
    g.units.map((u) => ({ id: u.id, name: u.symbol ? `${u.name} (${u.symbol})` : u.name, symbol: u.symbol }))
  ),
};

const UNIVERSAL: Attribute[] = [ATTR_COLOR];
const DEPT_EXTRAS: Record<string, Attribute[]> = {
  "01": [ATTR_PACKAGING, ATTR_UNIT],
};

export const findNodePath = (
  id: string,
  forest: CategoryItem[] = DEPARTMENTS_ATTR_TREE
): CategoryItem[] | null => {
  const walk = (nodes: CategoryItem[], trail: CategoryItem[], target: string): CategoryItem[] | null => {
    for (const n of nodes) {
      const next = [...trail, n];
      if (n.id === target) return next;
      if (n.children?.length) {
        const hit = walk(n.children, next, target);
        if (hit) return hit;
      }
    }
    return null;
  };

  let probe = id;
  while (probe && probe.length >= 2) {
    const hit = walk(forest, [], probe);
    if (hit) return hit;
    probe = probe.slice(0, -2);
  }
  return null;
};

/** Just the node (falls back to the nearest ancestor for deeper ids). */
export const findNode = (
  id: string,
  forest: CategoryItem[] = DEPARTMENTS_ATTR_TREE
): CategoryItem | null => {
  const path = findNodePath(id, forest);
  return path ? path[path.length - 1] : null;
};

/** All attributes that apply to a category id: each ancestor's own
    attributes from root → node. On duplicate attribute NAME, the
    deepest (most specific) node wins and is ordered first. */
export const attributesForCategory = (
  categoryId: string | null | undefined,
  forest: CategoryItem[] = DEPARTMENTS_ATTR_TREE
): Attribute[] => {
  const path = categoryId ? findNodePath(categoryId, forest) : null;
  if (!path) return [];

  const byName = new Map<string, Attribute>();

  for (let i = path.length - 1; i >= 0; i--) {
    for (const a of path[i].attributes ?? []) {
      const hit = byName.get(a.name);
      if (!hit) byName.set(a.name, { ...a });
      else {
        if (!hit.options?.length && a.options?.length) hit.options = a.options;
        if (!hit.unit && a.unit) hit.unit = a.unit;
      }
    }
  }

  const deptId = path[0]?.id;
  for (const a of [...(DEPT_EXTRAS[deptId] ?? []), ...UNIVERSAL]) {
    if (!byName.has(a.name)) byName.set(a.name, { ...a });
  }

  return [...byName.values()];
};

/** Required attributes only (for validation before publish). */
export const requiredAttributesForCategory = (
  categoryId: string | null | undefined,
  forest: CategoryItem[] = DEPARTMENTS_ATTR_TREE
): Attribute[] => attributesForCategory(categoryId, forest).filter((a) => a.required);